import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { create } from "@/lib/store";
import { DEFAULT_COLUMNS } from "@/pages/formats/constants/defaults";
import {
  BusinessUnitToggle,
  GeoViewToggle,
  MetricType,
  ROIBreakdownEnum,
  ViewToggle,
} from "@/shared/types";
import { hashStorage } from "@/shared/utils/hashStorage";
import { Funnel, FunnelType } from "@/Utils/hierarchy";

const STORE_NAME = "Formats";

interface FormatsState {
  filters: {
    targetNode: string | null;
    channels: string[];
    formats: string[];
    geographies: string[];
  };
  chart: {
    chartView: ViewToggle;
    chartBreakdown: ROIBreakdownEnum;
    isAscending: boolean;
    filterInsufficientData: boolean;
  };
  grid: {
    gridView: ViewToggle;
    geoView: GeoViewToggle;
    businessUnitToggle: BusinessUnitToggle;
    metricType: MetricType;
    activeColumns: string[];
    spend: FunnelType[];
    revenue: FunnelType[];
    roi: FunnelType[];
  };
}

interface FormatsActions {
  setTargetNode: (node: string | null) => void;
  setChannels: (channels: string[]) => void;
  setFormats: (formats: string[]) => void;
  setChartView: (view: ViewToggle) => void;
  setChartBreakdown: (breakdown: ROIBreakdownEnum) => void;
  setIsAscending: (isAscending: boolean) => void;
  setFilterInsufficientData: (filterInsufficientData: boolean) => void;
  setGridView: (view: ViewToggle) => void;
  setGeoView: (geoView: GeoViewToggle) => void;
  setBusinessUnitToggle: (businessUnitToggle: BusinessUnitToggle) => void;
  setMetricType: (metricType: MetricType) => void;
  setSpend: (spend: FunnelType[]) => void;
  setRevenue: (revenue: FunnelType[]) => void;
  setROI: (roi: FunnelType[]) => void;
  setGeographies: (geographies: string[]) => void;
  setActiveColumns: (columns: string[]) => void;
}

type FormatsStore = FormatsState & FormatsActions;

const defaultState: FormatsState = {
  filters: {
    targetNode: null,
    channels: [],
    formats: [],
    geographies: [],
  },
  chart: {
    chartView: ViewToggle.INSIGHT,
    chartBreakdown: ROIBreakdownEnum.SPEND_DOLLARS,
    isAscending: true,
    filterInsufficientData: true,
  },
  grid: {
    gridView: ViewToggle.INSIGHT,
    geoView: GeoViewToggle.AGGREGATE,
    businessUnitToggle: BusinessUnitToggle.AGGREGATE,
    metricType: MetricType.MARKETING_ROI,
    activeColumns: DEFAULT_COLUMNS,
    spend: [Funnel.Total],
    revenue: [Funnel.Total],
    roi: [Funnel.Total],
  },
};

export const useFormatsStore = create<FormatsStore>()(
  devtools(
    persist(
      immer((set) => ({
        ...defaultState,
        setTargetNode: (node) =>
          set((state) => {
            state.filters.targetNode = node;
          }),
        setChannels: (channels) =>
          set((state) => {
            state.filters.channels = channels;
          }),
        setFormats: (formats) =>
          set((state) => {
            state.filters.formats = formats;
          }),
        setGeographies: (geographies) =>
          set((state) => {
            state.filters.geographies = geographies;
          }),
        setChartView: (view) =>
          set((state) => {
            state.chart.chartView = view;
          }),
        setChartBreakdown: (breakdown) =>
          set((state) => {
            state.chart.chartBreakdown = breakdown;
          }),
        setIsAscending: (isAscending) =>
          set((state) => {
            state.chart.isAscending = isAscending;
          }),
        setFilterInsufficientData: (filterInsufficientData) =>
          set((state) => {
            state.chart.filterInsufficientData = filterInsufficientData;
          }),
        setGridView: (view) =>
          set((state) => {
            state.grid.gridView = view;
          }),
        setGeoView: (geoView) =>
          set((state) => {
            state.grid.geoView = geoView;
          }),
        setBusinessUnitToggle: (businessUnitToggle) =>
          set((state) => {
            state.grid.businessUnitToggle = businessUnitToggle;
          }),
        setMetricType: (metricType) =>
          set((state) => {
            state.grid.metricType = metricType;
          }),
        setSpend: (spend) =>
          set((state) => {
            state.grid.spend = spend;
          }),
        setRevenue: (revenue) =>
          set((state) => {
            state.grid.revenue = revenue;
          }),
        setROI: (roi) =>
          set((state) => {
            state.grid.roi = roi;
          }),
        setActiveColumns: (columns) =>
          set((state) => {
            state.grid.activeColumns = columns;
          }),
      })),
      {
        name: STORE_NAME,
        storage: createJSONStorage(() => hashStorage),
      }
    ),
    { name: STORE_NAME }
  )
);
